import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Clock3,
  FolderKanban,
  RefreshCw,
  ShieldAlert,
  UploadCloud
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type {
  TechOpsAttentionProject,
  TechOpsImportActivity,
  TechOpsOrganizationSetup
} from "../../lib/types";

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-IN").format(value);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function statusTone(status: string) {
  if (status === "FAILED" || status === "HIGH") {
    return "danger";
  }
  if (status === "PARSING" || status === "UPLOADED" || status === "NEEDS_REVIEW") {
    return "warning";
  }
  if (status === "ACTIVE" || status === "COMPLETED") {
    return "success";
  }
  return "neutral";
}

function importLabel(value: string) {
  switch (value) {
    case "RATE_CARD_IMPORT":
      return "Rate card";
    case "ORG_LOCATIONS":
      return "Locations";
    case "ORG_CATALOGUE":
      return "Catalogue";
    default:
      return value.replace(/_/g, " ");
  }
}

function SummaryCard({
  label,
  value,
  detail,
  tone,
  icon
}: {
  label: string;
  value: number;
  detail: string;
  tone: "blue" | "green" | "amber" | "red";
  icon: ReactNode;
}) {
  return (
    <div className="pa-summary-card">
      <span className={`pa-summary-icon ${tone}`}>{icon}</span>
      <small>{label}</small>
      <strong>{formatNumber(value)}</strong>
      <em>{detail}</em>
    </div>
  );
}

function EmptyRows({ label }: { label: string }) {
  return (
    <tr>
      <td colSpan={6} className="pa-empty-cell">{label}</td>
    </tr>
  );
}

function AttentionRows({ projects }: { projects: TechOpsAttentionProject[] }) {
  if (projects.length === 0) {
    return <EmptyRows label="No projects currently require platform attention." />;
  }
  return projects.map((project) => (
    <tr key={project.project_id}>
      <td>
        <strong>{project.project_name}</strong>
        <small>{project.organization_name}</small>
      </td>
      <td>{project.reason}</td>
      <td><span className={`pa-badge ${statusTone(project.risk_level)}`}>{project.risk_level}</span></td>
      <td>{formatNumber(project.attention_task_count)}</td>
      <td>{formatNumber(project.pending_approval_count)}</td>
      <td>{formatDateTime(project.updated_at)}</td>
    </tr>
  ));
}

function OrganizationRows({ organizations }: { organizations: TechOpsOrganizationSetup[] }) {
  if (organizations.length === 0) {
    return <tr><td colSpan={5} className="pa-empty-cell">No organizations are available.</td></tr>;
  }
  return organizations.map((organization) => (
    <tr key={organization.organization_id}>
      <td>
        <strong>{organization.organization_name}</strong>
        <small>{organization.organization_type}</small>
      </td>
      <td><span className={`pa-badge ${statusTone(organization.status)}`}>{organization.status}</span></td>
      <td>{formatNumber(organization.active_project_count)} / {formatNumber(organization.total_project_count)}</td>
      <td>{formatNumber(organization.location_count)}</td>
      <td>{formatNumber(organization.catalogue_item_count)}</td>
    </tr>
  ));
}

function ImportRows({ imports }: { imports: TechOpsImportActivity[] }) {
  if (imports.length === 0) {
    return <EmptyRows label="No active or failed imports." />;
  }
  return imports.map((item) => (
    <tr key={item.import_id}>
      <td>
        <strong>{item.file_name}</strong>
        <small>{item.organization_name}</small>
      </td>
      <td>{importLabel(item.import_type)}</td>
      <td><span className={`pa-badge ${statusTone(item.status)}`}>{item.status}</span></td>
      <td>{formatNumber(item.total_rows)}</td>
      <td>{formatNumber(item.error_rows)}</td>
      <td>{formatDateTime(item.updated_at)}</td>
    </tr>
  ));
}

export default function TechOpsDashboardPage() {
  const dashboardQuery = useQuery({
    queryKey: ["tech-ops-dashboard"],
    queryFn: api.getDashboard
  });

  if (dashboardQuery.isLoading) {
    return (
      <div className="pa-route-loading" aria-live="polite" aria-label="Loading platform dashboard">
        <div className="pa-skeleton-line" />
        <div className="pa-skeleton-grid">
          <div />
          <div />
          <div />
        </div>
      </div>
    );
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    const error = dashboardQuery.error;
    const platformAccessRequired = error instanceof ApiError && error.code === "PLATFORM_ACCESS_REQUIRED";
    return (
      <div className="pa-error-state">
        <span>{platformAccessRequired ? <ShieldAlert size={34} /> : <AlertCircle size={34} />}</span>
        <h1>{platformAccessRequired ? "Platform access required" : "Dashboard unavailable"}</h1>
        <p>{platformAccessRequired ? "Your login is valid, but it is not mapped to a TECH_OPS or TECH_ADMIN platform role." : "The dashboard could not be loaded from the Coprint backend."}</p>
        <button type="button" className="pa-primary-button" onClick={() => dashboardQuery.refetch()}>
          <RefreshCw size={17} />
          Retry
        </button>
      </div>
    );
  }

  const dashboard = dashboardQuery.data;
  const summary = dashboard.summary;

  return (
    <div className="pa-page-stack">
      <div className="pa-title-row">
        <div>
          <p className="pa-eyebrow">Tech Operations</p>
          <h1>Platform dashboard</h1>
          <p>PII-safe operational health across organizations, projects, approvals and imports.</p>
        </div>
        <div className="pa-user-chip">
          <span>{dashboard.platform_user.display_name.slice(0, 1).toUpperCase()}</span>
          <div>
            <strong>{dashboard.platform_user.display_name}</strong>
            <small>{dashboard.platform_user.roles.join(", ")}</small>
          </div>
        </div>
      </div>

      <section className="pa-summary-grid">
        <SummaryCard label="Organizations" value={summary.organization_count} detail="All tenant records" tone="blue" icon={<Building2 size={22} />} />
        <SummaryCard label="Active Projects" value={summary.active_project_count} detail="In tenant workflows" tone="green" icon={<FolderKanban size={22} />} />
        <SummaryCard label="Requires Action" value={summary.projects_requiring_action_count} detail="Risk or task signal" tone="red" icon={<AlertCircle size={22} />} />
        <SummaryCard label="Pending Approvals" value={summary.pending_approval_count} detail="Open approval tasks" tone="amber" icon={<CheckCircle2 size={22} />} />
        <SummaryCard label="Imports Processing" value={summary.imports_processing_count} detail="Uploaded or parsing" tone="blue" icon={<UploadCloud size={22} />} />
        <SummaryCard label="Failed Imports" value={summary.failed_import_count} detail="Needs operator review" tone="red" icon={<Clock3 size={22} />} />
      </section>

      <section className="pa-dashboard-grid">
        <div className="pa-panel">
          <div className="pa-panel-header">
            <h2>Attention Queue</h2>
            <span>Updated {formatDateTime(dashboard.generated_at)}</span>
          </div>
          <div className="pa-table-scroll">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Reason</th>
                  <th>Risk</th>
                  <th>Signals</th>
                  <th>Approvals</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <AttentionRows projects={dashboard.attention_projects} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="pa-panel">
          <div className="pa-panel-header">
            <h2>Import Activity</h2>
          </div>
          <div className="pa-table-scroll">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>Errors</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <ImportRows imports={dashboard.import_activity} />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pa-panel">
        <div className="pa-panel-header">
          <h2>Organization Setup</h2>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Status</th>
                <th>Projects</th>
                <th>Locations</th>
                <th>Catalogue Items</th>
              </tr>
            </thead>
            <tbody>
              <OrganizationRows organizations={dashboard.organization_setups} />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
